import { Component, computed, inject, input } from '@angular/core';
import { ThemeService, type ThemeMode } from '@sb/shared/theme';
import { clsx } from 'clsx';

export type LogoVariant = 'lockup' | 'wordmark' | 'icon';

type LogoAssetSet = Record<ThemeMode, string>;

const ON_SURFACE_ASSETS: Record<LogoVariant, LogoAssetSet> = {
  lockup: {
    light: '/assets/images/shiftbase-aurora-lockup.svg',
    dark: '/assets/images/shiftbase-aurora-lockup-dark.svg',
  },
  wordmark: {
    light: '/assets/images/shiftbase-aurora-wordmark.svg',
    dark: '/assets/images/shiftbase-aurora-wordmark-dark.svg',
  },
  icon: {
    light: '/assets/images/shiftbase-aurora-icon.svg',
    dark: '/assets/images/shiftbase-aurora-icon.svg',
  },
};

const ON_BRAND_ASSETS: Record<LogoVariant, LogoAssetSet> = {
  lockup: {
    light: '/assets/images/shiftbase-aurora-lockup-dark.svg',
    dark: '/assets/images/shiftbase-aurora-lockup-dark.svg',
  },
  wordmark: {
    light: '/assets/images/shiftbase-aurora-wordmark-dark.svg',
    dark: '/assets/images/shiftbase-aurora-wordmark-dark.svg',
  },
  icon: {
    light: '/assets/images/shiftbase-aurora-icon.svg',
    dark: '/assets/images/shiftbase-aurora-icon.svg',
  },
};

/**
 * The Shiftbase brand mark, theme-aware via {@link ThemeService}: it renders the
 * single asset for the active mode and reacts to a live theme switch through the
 * service's signals. Storybook's theme toolbar drives the same service, so
 * stories preview each theme too.
 *
 * Size it by WIDTH on the host — the height follows each asset's own ratio:
 *
 * ```html
 * <sb-logo class="w-36" />
 * <sb-logo class="w-40" [variant]="'wordmark'" [onBrand]="true" />
 * <sb-logo class="size-full" [variant]="'icon'" />
 * ```
 */
@Component({
  selector: 'sb-logo',
  templateUrl: './logo.component.html',
  host: {
    '[class]': 'hostClasses',
  },
})
export class LogoComponent {
  #theme = inject(ThemeService);

  /** lockup = mark + wordmark, wordmark = text only, icon = mark only. */
  variant = input<LogoVariant>('lockup');

  /**
   * Set when the logo sits on a brand-colored fill (e.g. a `bg-brand` header
   * strip): picks the white/on-dark treatment in every theme.
   */
  onBrand = input(false);

  /** Alternative text for the rendered logo. Pass an empty string when decorative. */
  alt = input('Shiftbase');

  hostClasses = clsx('inline-flex');

  #assets = computed(() => (this.onBrand() ? ON_BRAND_ASSETS : ON_SURFACE_ASSETS)[this.variant()]);

  src = computed(() => this.#assets()[this.#theme.mode()]);

  imgClasses = computed(() =>
    // The icon asset is a colour gradient; on a brand fill it is recoloured to solid white.
    clsx('h-auto w-full', this.variant() === 'icon' && this.onBrand() && 'brightness-0 invert'),
  );
}
